import { useEffect, useState } from "react";

export type ConfettiTier = 2 | 3;

interface ConfettiProps {
  triggerKey: number | null;
  tier?: ConfettiTier;
}

interface Particle {
  id: number;
  x: number;
  y: number;
  rot: number;
  hue: string;
  size: number;
  delay: number;
}

const HUES = [
  "hsl(var(--primary))",
  "hsl(var(--primary-glow))",
  "hsl(var(--success))",
  "hsl(var(--warning))",
];

function buildParticles(count: number): Particle[] {
  return Array.from({ length: count }).map((_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
    const distance = 80 + Math.random() * 180;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance - 40,
      rot: Math.random() * 720 - 360,
      hue: HUES[i % HUES.length],
      size: 4 + Math.random() * 6,
      delay: Math.random() * 80,
    };
  });
}

export function Confetti({ triggerKey, tier = 2 }: ConfettiProps) {
  const [active, setActive] = useState(false);
  const [particles, setParticles] = useState<Particle[]>([]);

  useEffect(() => {
    if (!triggerKey) return;
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const count = tier === 3 ? 60 : 30;
    setParticles(buildParticles(count));
    setActive(true);
    const t = window.setTimeout(() => setActive(false), tier === 3 ? 1400 : 900);
    return () => window.clearTimeout(t);
  }, [triggerKey, tier]);

  if (!active) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
    >
      {tier === 3 && (
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(50% 40% at 50% 45%, hsl(var(--primary-glow) / 0.35), transparent 70%)",
            animation: "breathe 1.2s ease-out forwards",
          }}
        />
      )}
      <div className="relative">
        {particles.map((p) => (
          <span
            key={p.id}
            className="confetti-particle"
            style={{
              ["--cx" as any]: `${p.x}px`,
              ["--cy" as any]: `${p.y}px`,
              ["--cr" as any]: `${p.rot}deg`,
              width: `${p.size}px`,
              height: `${p.size * 0.4}px`,
              background: p.hue,
              animationDelay: `${p.delay}ms`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
